import store from "../../store/store";
import indexDbService from "@/helpers/dbHelper";
import DashboardService from "../../services/dashboard-service";

// A full membership card code: the "MEM" prefix followed by at least 4 digits
// (e.g. "MEM31232131"). Kept strict so that a person typing "mem" (or searching
// for a customer whose name contains it) in the customer search does not trip the
// eos_memberscan auto search/create/select flow mid-keystroke - only a complete
// scanned/typed code qualifies.
const MEMBER_CODE = /^MEM\d{4,}$/i;

/**
 * True when a scanned/typed search value is a complete membership card code
 * (e.g. "MEM31232131"), gating the eos_memberscan auto search/create/select flow.
 */
export function isMembershipScanValue(value) {
  return MEMBER_CODE.test(String(value || "").trim());
}

/**
 * Search for a customer matching a scanned membership code, or create one if
 * none exists, and return it. There is no dedicated membership-id field on the
 * customer object yet, so the raw code is kept inside `first_name` (new
 * customers get `Member <code>`) which also lets the existing `search=` param
 * find the same customer again on a repeat scan.
 *
 * Mirrors the phone-based auto add-customer flow in StandardOrder.vue /
 * StandardOrderCheckout.vue's `handleCdPosUpdate` (local cache -> API search ->
 * fallback create), just keyed on the membership code instead of a phone number.
 */
export async function resolveMemberScanCustomer(code) {
  const trimmedCode = String(code || "").trim();
  const isCodeMatch = (customer) =>
    !!(customer && customer.first_name && customer.first_name.toUpperCase().includes(trimmedCode.toUpperCase()));

  let foundCustomer = null;

  try {
    const cached = await indexDbService.getDbData("customers");
    const cachedCustomers = Array.isArray(cached) ? cached : [];
    foundCustomer = cachedCustomers.find(isCodeMatch);
  } catch (e) {
    console.error(e);
  }

  if (!foundCustomer && store.getters.connection) {
    try {
      const response = await DashboardService.getCustomersList({
        search: trimmedCode,
        page: 1
      });
      const results = (response && response.results) ? response.results : [];
      foundCustomer = results.find(isCodeMatch) || results[0] || null;

      if (foundCustomer) {
        indexDbService.saveToDb("customers", foundCustomer).then();
        store.commit("customers/save", foundCustomer);
      }
    } catch (e) {
      console.error(e);
    }
  }

  if (foundCustomer) {
    return foundCustomer;
  }

  const newCustomer = {
    first_name: trimmedCode,
    last_name: null,
    phone: null,
    email: null,
    notes: null,
    local_id: (new Date()).toISOString(),
    addresses: [],
    default_address: null
  };

  // Save locally first so the scan still selects a customer if the network call fails.
  store.commit("customers/save", newCustomer);
  indexDbService.saveToDb("customers", newCustomer).then();

  // Create the member on the server on the spot so the order references a synced
  // customer (with its real id) instead of a local-only record.
  if (store.getters.connection) {
    try {
      const created = await store.dispatch("customers/createCustomer", newCustomer);
      if (created && created.id) {
        return created;
      }
    } catch (e) {
      console.error(e);
    }
  }

  return newCustomer;
}
